import {
	Icon,
	IconBus,
	IconCoffee,
	IconForbidden,
	IconGhost,
	IconLock,
	IconTrip,
} from '@flixbus/honeycomb-icons-react';

import {
	ACTIVITY_GRAHQL_TYPES,
	ACTIVITY_LAYOUT_VARIANTS,
	ACTIVITY_RENDER_TYPES,
	RRO_TIME_OFF_ABSENCE_REASON_NAME,
} from 'common/ActivityChip/const';
import {
	getExtraActivityIcon,
	isExtraActivityType,
} from 'common/ActivityChip/utils/extraActivities';

const WORK_ASSIGNMENT_ACTIVITY_CONFIGS = {
	[ACTIVITY_RENDER_TYPES.deadhead]: {
		header: 'Deadhead',
		InlineIcon: IconGhost,
	},
	[ACTIVITY_RENDER_TYPES.traveling]: {
		header: 'Traveling',
		InlineIcon: IconTrip,
	},
	[ACTIVITY_RENDER_TYPES.protection]: {
		header: 'Protection',
		InlineIcon: IconLock,
		getSubline: ({ sublineFrom }) => sublineFrom,
	},
};

const getActivityConfig = (data) => {
	const { fromTimestamp, toTimestamp, activity } = data;

	switch (activity.__typename) {
		case ACTIVITY_GRAHQL_TYPES.BREAK:
			return handleBreakItem(data);

		case ACTIVITY_GRAHQL_TYPES.WORK_ASSIGNMENT:
			return handleCrewMemberWorkAssignmentActivityItem(data);

		case ACTIVITY_GRAHQL_TYPES.ABSENCE:
			return handleAbsenceItem(data);

		case ACTIVITY_GRAHQL_TYPES.TAIL:
			return {
				fromTimestamp,
				toTimestamp,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.shiftTail,
			};

		default:
			return {
				renderType: ACTIVITY_RENDER_TYPES.absence,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.absence,
				header: 'Unknown activity',
				subline: '',
				title: 'Unknown',
				fromTimestamp,
				toTimestamp,
			};
	}
};

const handleBreakItem = (data) => {
	const { activity, fromTimestamp, toTimestamp } = data;
	const { idle, rest, absence } = ACTIVITY_RENDER_TYPES;

	switch (activity.breakType) {
		case 'rest':
			return {
				raw: activity,
				renderType: rest,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.rest,
				header: 'Rest',
				subline: '',
				icon: <Icon InlineIcon={IconCoffee} />,
				fromTimestamp,
				toTimestamp,
			};
		case 'idle':
			return {
				renderType: idle,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.idle,
				header: 'Idle',
				subline: '',
				icon: null,
				fromTimestamp,
				toTimestamp,
				raw: activity,
			};
		default:
			return {
				renderType: absence,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.absence,
				header: 'Unknown Break',
				subline: activity.breakType || '',
				fromTimestamp,
				toTimestamp,
			};
	}
};

const handleCrewMemberWorkAssignmentActivityItem = (waItem) => {
	const { activity, fromTimestamp, toTimestamp, crewMemberId } = waItem;
	const { fromStation, toStation, title, activityType } = activity;

	const { assignment, planned, extra } = ACTIVITY_RENDER_TYPES;

	const waItemConfig = {
		renderType: null,
		layoutVariant: ACTIVITY_LAYOUT_VARIANTS.activity,
		header: title,
		subline: null,
		icon: <Icon InlineIcon={IconBus} />,
		fromTimestamp,
		toTimestamp,
		raw: activity,
	};

	const sublineFrom = fromStation !== null ? fromStation?.code : '...';
	const sublineTo = toStation !== null ? toStation?.code : '...';

	waItemConfig.subline = `${sublineFrom} → ${sublineTo}`;

	if (isExtraActivityType(activityType)) {
		waItemConfig.renderType = extra;
		waItemConfig.layoutVariant = ACTIVITY_LAYOUT_VARIANTS.extraActivity;
		waItemConfig.header = sublineFrom;
		waItemConfig.icon = getExtraActivityIcon(activityType);
	} else if (WORK_ASSIGNMENT_ACTIVITY_CONFIGS[activityType]) {
		const { header, InlineIcon, getSubline } = WORK_ASSIGNMENT_ACTIVITY_CONFIGS[activityType];

		waItemConfig.header = header;
		waItemConfig.renderType = activityType;
		waItemConfig.layoutVariant = ACTIVITY_LAYOUT_VARIANTS.activity;
		waItemConfig.icon = <Icon InlineIcon={InlineIcon} />;

		if (getSubline) {
			waItemConfig.subline = getSubline({ sublineFrom, sublineTo });
		}
	} else {
		waItemConfig.renderType =
			activity.status !== 'planned' && activity.status !== 'created' ? assignment : planned;
		waItemConfig.layoutVariant = ACTIVITY_LAYOUT_VARIANTS.activity;
	}

	if (crewMemberId) {
		waItemConfig.crewMemberId = crewMemberId;
	}

	return waItemConfig;
};

const handleAbsenceItem = (data) => {
	const { fromTimestamp, toTimestamp, activity, crewMemberId } = data;
	const { reason, availableForAssignments } = activity;

	const absenceConfig = {
		renderType: ACTIVITY_RENDER_TYPES.absence,
		layoutVariant: ACTIVITY_LAYOUT_VARIANTS.absence,
		header: 'Unavailable',
		subline: reason.name || '',
		icon: <Icon InlineIcon={IconForbidden} />,
		fromTimestamp,
		toTimestamp,
		crewMemberId,
		raw: activity,
	};

	switch (availableForAssignments) {
		case true:
			return {
				renderType: ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityEnabled,
				layoutVariant: ACTIVITY_LAYOUT_VARIANTS.rroTimeOffAvailabilityEnabled,
				header: 'Available for assignment',
				crewMemberId,
				fromTimestamp,
				toTimestamp,
				raw: activity,
			};
		case false:
			if (reason.name === RRO_TIME_OFF_ABSENCE_REASON_NAME) {
				return {
					renderType: ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityDisabled,
					layoutVariant: ACTIVITY_LAYOUT_VARIANTS.rroTimeOffAvailabilityDisabled,
					header: 'Time off',
					crewMemberId,
					fromTimestamp,
					toTimestamp,
					raw: activity,
				};
			}

			return absenceConfig;
		default:
			return absenceConfig;
	}
};

export default getActivityConfig;
